

































































import Vue from 'vue';
import { mapActions, mapMutations, mapState } from 'vuex';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import { Source } from '@/constants/source';
import { HotelStatus } from '@/constants/hotelStatus';
import { PaymentSystem } from '@/constants/paymentSystem';
import { Pipe } from '@/constants/pipe';
import { HotelInterface } from '@/types/hotelInterface';
import hashBookingDataMixin from '@/mixins/hashBookingData';
import LoaderData from '@/components/common/LoaderData.vue';
import DatePicker from '@/components/landing/DatePicker.vue';
import RoomTypeCard from '@/components/landing/RoomTypeCard.vue';
import LegalInfoModal from '@/modals/landing/LegalInfoModal.vue';
import BookingDatesModal from '@/modals/landing/BookingDatesModal.vue';
import { HashedBookingDataInterface } from '@/types/pages/landing/hashedBookingDataInterface';
import { BookingDataInterface } from '@/types/store/landing/booking/bookingDataInterface';
import { FormDataInterface } from '@/types/pages/landing/hotelLandingInterface';
import { GetRoomTypesInterface } from '@/types/api/landing/roomTypesInterface';
import { BookingDetailsInterface } from '@/types/store/landing/booking/bookingDetailsInterface';
import { RatePlanInterface, RoomDataInterface } from '@/types/store/landing/hotels/roomDataInterface';
import { PrefectureInterface } from '@/types/common/prefectureInterface';

export default Vue.extend({
  name: 'HotelLanding',
  components: {
    LoaderData,
    DatePicker,
    RoomTypeCard,
    LegalInfoModal,
    BookingDatesModal,
  },
  mixins: [validationMixin, hashBookingDataMixin],
  data: () => ({
    form: {} as FormDataInterface,
    availableRoomTypes: [] as RoomDataInterface[],
    dataLoading: false,
    roomTypeChanged: false,
    showDatePicker: false,
    titleOpen: false,
    roomTypeOpen: false,
    rangeError: false,
  }),
  computed: {
    ...mapState({
      paymentSystem: (state: any): PaymentSystem => state.landing.settings.payment_system,
      accountSource: (state: any): Source => state.landing.settings.account_source,
      booking: (state: any): BookingDataInterface => state.landing.booking.initial,
      hotel: (state: any): HotelInterface => state.landing.hotel.item,
      prefectures: (state: any): PrefectureInterface => state.landing.settings.prefectures,
      isDemoLanding: (state: any): boolean => state.landing.settings.testing_mode,
    }),
    isTlLinkoln(): boolean {
      return this.accountSource === Source.TlLincoln;
    },
    isStripe(): boolean {
      return this.paymentSystem === PaymentSystem.Stripe;
    },
    isGMO(): boolean {
      return this.paymentSystem === PaymentSystem.GMO;
    },
    maxCheckOutDate(): Date {
      const maxCheckOutDate = this.form.checkin ? new Date(this.form.checkin) : new Date();
      maxCheckOutDate.setHours(0, 0, 0, 0);
      return new Date(maxCheckOutDate.setMonth(maxCheckOutDate.getDay() + 60));
    },
    hotelName(): string {
      return this.hotel.name[this.i18nTag()] ? this.hotel.name[this.i18nTag()] : this.hotel.name.en;
    },
    notPublished(): boolean {
      return (
        this.hotel &&
        (this.hotel.publishing_status === HotelStatus.Draft || this.hotel.publishing_status === HotelStatus.TestMode)
      );
    },
    guestsCount(): number {
      return this.hotel.max_room_types_capacity ? this.hotel.max_room_types_capacity : 1;
    },
    minDate(): Date {
      const minDate = new Date();
      minDate.setHours(0, 0, 0, 0);
      if (this.isCheckinUntilTimeUp(this.hotel.checkin_until!)) {
        minDate.setDate(new Date().getDate() + 1);
      }

      return minDate;
    },
    endDate(): Date {
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 180);
      endDate.setHours(0, 0, 0, 0);
      return endDate;
    },
    translationParams(): any {
      return this.hotel.cancellation_policy ? this.hotel.cancellation_policy : {};
    },
    isFreeCancellation(): boolean {
      return this.translationParams.default_rate == 0;
    },
    hotelAddressLink(): string {
      const lat = this.hotel.latitude;
      const lng = this.hotel.longitude;
      return `dir//${lat},${lng}/@${lat},${lng},12z`;
    },
    hotelAddress(): string {
      return this.hotel.address[this.i18nTag()] ? this.hotel.address[this.i18nTag()] : this.hotel.address.en;
    },
    hotelAddressFormatted(): string {
      let prefecture: any = '';
      if (this.hotel.prefecture_code) {
        prefecture = this.prefectures[this.hotel.prefecture_code];
        if (prefecture) {
          prefecture = this.$t('common.prefecture.' + prefecture);
        }
      }
      if (this.$i18n.locale === 'en') {
        return prefecture ? `${this.hotelAddress}, ${prefecture}` : this.hotelAddress;
      }
      return `${prefecture}${this.hotelAddress}`;
    },
  },
  watch: {
    'form.guests_number'(newV, oldV) {
      if (newV && newV !== oldV) {
        this.getRoomTypeInfo();
      }
    },
    'form.checkin'(newV, oldV) {
      if (newV && newV !== oldV && this.form.checkout) {
        const checkin = new Date(newV);
        const checkout = new Date(this.form.checkout);
        if (checkin >= checkout || checkout > this.maxCheckOutDate) {
          this.$set(this.form, 'checkout', Vue.filter('date')(Vue.filter('nextDay')(checkin)));
        }
      }
    },
  },
  created() {
    this.clearBookingState(this.booking);
    const hashedBooking: HashedBookingDataInterface = (this as any).parseHashedBookingData();
    this.form = hashedBooking ? { ...hashedBooking } : { ...this.booking };
  },
  validations: {
    form: {
      room_type_hid: { required },
      checkin: { required },
      checkout: { required },
      guests_number: { required },
    },
  },
  methods: {
    ...mapMutations({
      setRoomInfo: 'landing/hotel/setRoomInfo',
      setBooking: 'landing/booking/setBooking',
      setBookingDetails: 'landing/booking/setBookingDetails',
    }),
    ...mapActions({
      getRoomTypes: 'landing/hotel/getRoomTypeInfo',
      clearBookingState: 'landing/booking/resetState',
    }),
    updateHash(): string {
      const hashArr: string[] = [this.form.checkin, this.form.checkout, this.form.guests_number.toString()];
      if (this.form.meal_option) {
        hashArr.push(this.form.meal_option.toString());
      }
      if (this.form.room_type_hid) {
        hashArr.push(this.form.room_type_hid);
      }
      const hash = hashArr.join(Pipe.BookingDataHash);
      window.location.hash = hash;
      return hash;
    },
    getRoomTypeInfo(): void {
      this.dataLoading = true;
      if (this.roomTypeChanged) {
        this.roomTypeChanged = false;
      }
      this.updateHash();
      const params: GetRoomTypesInterface = {
        hotel_hid: this.hotel.hotel_hid as string,
        guests_number: this.form.guests_number,
        checkin: Vue.filter('date')(this.form.checkin),
        checkout: Vue.filter('date')(this.form.checkout),
        demo_mode: this.isDemoLanding,
      };
      this.getRoomTypes(params)
        .then(res => {
          if (res.data && res.data.length) {
            this.roomTypeChanged = true;
            const rate_plan_id: string[] | string = window.location.pathname.split('/landing/');
            if (rate_plan_id.length > 1) {
              const hashedData = rate_plan_id[1].split('-');
              if (hashedData.length > 2) {
                res.data.sort((a: RoomDataInterface, b: RoomDataInterface) => {
                  if (a.rate_plans.map((m: RatePlanInterface) => m.rate_plan_hid).indexOf(hashedData[1]) !== -1) {
                    return -1;
                  }
                  if (b.rate_plans.map((m: RatePlanInterface) => m.rate_plan_hid).indexOf(hashedData[1]) !== -1) {
                    return 1;
                  }
                  return 0;
                });
              }
            }
            this.availableRoomTypes = res.data;
          } else {
            this.availableRoomTypes = [];
          }
          if (window && typeof (window as any).gtag !== 'undefined') {
            (window as any).gtag('event', 'select_content', {
              content_type: 'product',
              items: [params],
              value: !!res.data.length,
            });
          }
        })
        .finally(() => {
          (this as any).dataLoading = false;
        });
    },
    changeDates(data: any): void {
      if (typeof data !== 'string') {
        if (data.from.split('-').length > 2) {
          data.from = data.from.split('-').join('/');
          data.to = data.to.split('-').join('/');
        }
        this.form.checkin = data.from;
        this.form.checkout = data.to;
      }
      this.getRoomTypeInfo();
      this.showDatePicker = false;
    },
    selectPlan(room: RoomDataInterface, rate: RatePlanInterface) {
      this.form.room_type_hid = room.hid;
      this.form.meal_option = rate.meal_options!.length;
      if (!this.validateBeforeSubmit('form') || this.rangeError) {
        return;
      }
      if (window && typeof (window as any).gtag !== 'undefined') {
        (window as any).gtag('event', 'proceed_to_booking', {
          items: [rate],
        });
      }
      const data: BookingDataInterface = JSON.parse(JSON.stringify(this.form));
      const bookingDetails: BookingDetailsInterface = {
        payment_method: rate.guarantee_type,
        total_amount: rate.amount_with_discount,
        use_discount: !!rate.discount_amount,
      };
      this.setRoomInfo(room);
      this.setBooking(data);
      this.setBookingDetails(bookingDetails);
      this.$router.push({ path: '/guest-info', hash: '#' + this.updateHash() });
    },
    isCheckinUntilTimeUp(checkin_until: string): boolean {
      const [hour, minute] = this.getHoursAndMinutes(checkin_until);
      const [currentHour, currentMinute] = this.getHoursAndMinutes(
        new Date().toLocaleString('en-US', {
          timeZone: 'Japan',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }),
      );

      if (hour < 10) return false;

      if (currentHour > hour) {
        return true;
      }

      if (currentHour === hour && currentMinute >= minute) {
        return true;
      }

      return false;
    },
    getHoursAndMinutes(str: string) {
      return str.split(':').map(s => Number(s));
    },
  },
});
